import { ArrowUpCircle } from 'lucide-react';
import { useBuildUpdateStore } from '@/stores/buildUpdateStore';

/**
 * The conspicuous half of the new-build prompt: a corner card offering an
 * explicit reload. Mount once inside the shell's content pane.
 *
 * Deliberately non-modal — no scrim, no focus steal, no `role="alert"`. It sits
 * over a live trading screen, so it announces politely (`role="status"`) and
 * leaves every control beneath it reachable.
 *
 * The `role="status"` wrapper stays mounted even with nothing to say. A live
 * region that appears already-populated is widely treated as a new subtree
 * rather than an update, and would go unannounced on the one occasion it
 * matters; the region has to pre-exist the message. Empty it renders nothing
 * and occupies no space.
 *
 * Bottom-LEFT is the one corner that is free. Order entry and cancellation live
 * in the GameDetailPanel's right rail, and every fixed overlay in the app
 * (PositionTracker toasts, the floating PositionSidebar) is right-anchored too.
 * The offsets clear the 64px nav rail and the status bar beneath it.
 *
 * Dark is the app's `:root` default and `dark:` is inert here, so the light
 * theme is addressed with `light:` (see tailwind.config.js).
 */
export function BuildUpdateBreakout() {
  const updateAvailable = useBuildUpdateStore((s) => s.updateAvailable);
  const breakoutDismissed = useBuildUpdateStore((s) => s.breakoutDismissed);
  const dismissBreakout = useBuildUpdateStore((s) => s.dismissBreakout);
  const reloadNow = useBuildUpdateStore((s) => s.reloadNow);

  const show = updateAvailable && !breakoutDismissed;

  return (
    <div role="status">
      {show && (
        <div
          data-testid="build-update-breakout"
          // Opaque `bg-card`, not the inline banner's `bg-amber-500/10`: this
          // floats over a dense market table, and a tinted-transparent panel let
          // the rows read straight through the copy. The amber lives in the
          // border and the heading instead, which carries the same "attention,
          // not error" reading.
          className="bg-card fixed bottom-10 left-20 z-40 max-w-sm rounded-lg border border-amber-500/40 px-4 py-3 text-[11px] shadow-lg"
        >
          <div className="flex items-center gap-2 font-medium text-amber-400 light:text-amber-700">
            <ArrowUpCircle size={14} className="shrink-0" />
            A new version of g1 is available
          </div>
          <p className="text-muted-foreground mt-1">
            Your open orders and positions are unaffected — reload whenever it suits you.
          </p>
          <div className="mt-2.5 flex items-center gap-2">
            <button
              type="button"
              onClick={reloadNow}
              className="rounded border border-amber-500/40 px-2 py-0.5 font-medium text-amber-400 hover:bg-amber-500/15 light:text-amber-700"
            >
              Reload now
            </button>
            <button
              type="button"
              onClick={dismissBreakout}
              className="text-muted-foreground hover:bg-accent hover:text-foreground rounded px-2 py-0.5"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
